import React, { useEffect, useState } from 'react';
import { differenceInSeconds } from 'date-fns';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import { useUser } from '../../../../../contexts/UserContext';
import { useNetwork } from '../../../../../utils/network';
import { useShowSnackbar } from '../../../../../utils/snackbar';
import CountdownTimer from '../CountdownTimer/CountdownTimer';
import { CaptchaModal } from '../../../../../components/CaptchaModal';

type FaucetProps = {
  // Kept for the call site; the widget now always renders the shared arena
  // panel (it only lives inside the dashboard's `.dash` scope).
  embedded?: boolean;
  /** Report whether the faucet is claimable now (dashboard FAB badge + item). */
  onReadyChange?: (ready: boolean) => void;
  /** Incrementing counter from the FAB's "Claim faucet" item → opens the captcha. */
  claimSignal?: number;
};

export const Faucet: React.FC<FaucetProps> = ({
  onReadyChange,
  claimSignal
}) => {
  const { user, setUser } = useUser();
  const { showSnackbar } = useShowSnackbar();
  const { post } = useNetwork();
  const nextFaucetTime = new Date(user.nextFaucetClaim);

  const [faucetClaimed, setFaucetClaimed] = useState<boolean>(
    nextFaucetTime ? differenceInSeconds(nextFaucetTime, new Date()) > 0 : false
  );

  const [nextFaucetClaim, setNextFaucetClaim] = useState<Date | null>(
    nextFaucetTime
  );
  const [countdownTimer, setCountdownTimer] = useState<number>(
    nextFaucetTime ? differenceInSeconds(nextFaucetTime, new Date()) : 0
  );
  const [captchaModalOpen, setCaptchaModalOpen] = useState(false);

  const handleClaimClick = () => {
    setCaptchaModalOpen(true);
  };

  const claimFaucet = async (captchaToken: string) => {
    try {
      const response = await post('/user/faucet', { captchaToken }, true);
      const { earnedTokens, nextClaimTime } = response;

      setUser({
        ...user,
        earnedTokens,
        nextFaucetClaim: nextClaimTime
      });

      setNextFaucetClaim(new Date(nextClaimTime));
      setCountdownTimer(
        differenceInSeconds(new Date(nextClaimTime), new Date())
      );
      setFaucetClaimed(true);
      setCaptchaModalOpen(false);
      showSnackbar('Faucet claimed successfully!', 'success');
    } catch (error: any) {
      showSnackbar(error.response.data.message, 'error');
      setCaptchaModalOpen(false);
    }
  };

  const handleCaptchaClose = () => {
    setCaptchaModalOpen(false);
  };

  useEffect(() => {
    if (countdownTimer > 0) {
      const timer = setTimeout(() => {
        setCountdownTimer(countdownTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setFaucetClaimed(false); // Allow user to claim again
    }
  }, [countdownTimer]);

  // Surface "ready to claim" up to the dashboard (FAB badge + quick-action item).
  useEffect(() => {
    onReadyChange?.(!faucetClaimed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faucetClaimed]);

  // The dashboard FAB's "Claim faucet" item opens the captcha via this signal.
  useEffect(() => {
    if (claimSignal && !faucetClaimed) setCaptchaModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimSignal]);

  return (
    <div className="dash-panel accent-cyan">
      <div className="dash-phead">
        <WaterDropIcon className="ic" /> INC Faucet
      </div>

      <div className="dash-pbody dash-center">
        {faucetClaimed ? (
          <>
            <div
              className="dash-unit"
              style={{ marginTop: 0, marginBottom: 14 }}
            >
              Next claim in
            </div>
            {nextFaucetClaim && (
              <CountdownTimer
                nextGiveaway={nextFaucetClaim}
                isTiny
                fieldsToShow={['minutes', 'seconds']}
              />
            )}
          </>
        ) : (
          <p className="dash-note" style={{ textAlign: 'center', margin: 0 }}>
            Claim your complimentary INC tokens — a fresh batch each cycle.
          </p>
        )}
      </div>

      <div className="dash-prow" style={{ justifyContent: 'center' }}>
        <button
          type="button"
          className="dash-btn solid"
          onClick={handleClaimClick}
          disabled={faucetClaimed}
          data-track="dashboard.faucet_claim"
        >
          {faucetClaimed ? 'Claimed' : 'Claim now'}
        </button>
      </div>

      <CaptchaModal
        open={captchaModalOpen}
        onClose={handleCaptchaClose}
        onSuccess={claimFaucet}
        title="Verify to Claim Faucet"
        description="Complete the verification to claim your INC tokens"
      />
    </div>
  );
};
